import { forwardRef } from 'react'
import type { InputHTMLAttributes } from 'react'

import { cn } from '@/lib/utils'

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  readonly id: string
  readonly label: string
  readonly error?: string | undefined
  readonly hint?: string | undefined
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { id, label, error, hint, required, className, ...props },
  ref,
) {
  const hintId = hint !== undefined ? `${id}-hint` : undefined
  const errorId = error !== undefined ? `${id}-error` : undefined
  const describedByIds = [hintId, errorId].filter((value): value is string => value !== undefined)
  const describedByProps =
    describedByIds.length > 0 ? { 'aria-describedby': describedByIds.join(' ') } : {}

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-foreground-secondary">
        {label}
        {required === true ? (
          <span aria-hidden="true" className="text-status-danger">
            {' '}
            *
          </span>
        ) : null}
      </label>
      <input
        ref={ref}
        id={id}
        required={required}
        aria-invalid={Boolean(error)}
        {...describedByProps}
        className={cn(
          'h-10 rounded-md border bg-background-elevated px-3 text-sm text-foreground-primary placeholder:text-foreground-muted',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
          'disabled:cursor-not-allowed disabled:opacity-60',
          error !== undefined ? 'border-status-danger' : 'border-border-default',
          className,
        )}
        {...props}
      />
      {hint !== undefined && error === undefined ? (
        <p id={hintId} className="text-xs text-foreground-muted">
          {hint}
        </p>
      ) : null}
      {error !== undefined ? (
        <p id={errorId} role="alert" className="text-xs text-status-danger">
          {error}
        </p>
      ) : null}
    </div>
  )
})
